import { AddComment } from "@/components/comments/AddComment.tsx";
import {
  ConfirmationModal,
  useModals,
  Box,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  LinkTo,
  Stack,
} from "@skailer-com/uikit";
import { useNotificationHook } from "@/hooks/use-notification.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ActionItem } from "@/pages/recruit/components/ActionItem.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import {
  useApplicantActionDelete,
  useApplicantActions,
} from "@/queries/recruit/use-applicant-actions.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiAction,
  ApiActionItemTypes,
  ApiActionTypes,
  ApiActionVisibilities,
} from "@/types/action.types.ts";
import { ApiEntityTypes } from "@/types/common.types.ts";
import { ApiApplicant } from "@/types/recruit/applicant.types.ts";
import { SourceTypes } from "@/types/recruit/source.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ApplicantProfileCommentsProps {
  applicant: ApiApplicant;
  disabledComments?: boolean;
  isNotesTabActive?: boolean;
}

export function ApplicantProfileComments({
  applicant,
  disabledComments = false,
  isNotesTabActive = false,
}: ApplicantProfileCommentsProps) {
  const { data: actions } = useApplicantActions(applicant.id);
  const { canIEdit } = usePermissions();
  const { data: employee } = useEmployeeMe();
  const modalsService = useModals();
  const { t } = useTranslation();
  const [editAction, setEditAction] = useState<ApiAction | undefined>(undefined);
  const { readActionsNotification } = useNotificationHook();

  useEffect(() => {
    if (isNotesTabActive && actions) {
      readActionsNotification(actions);
    }
  }, [actions, isNotesTabActive]);

  const canIManageCandidates = canIEdit(ApiPermission.manage_candidates);

  const canEditApplicantAction = (action: ApiAction) => {
    return (
      employee != undefined &&
      action.author?.id === employee?.id &&
      [ApiActionTypes.comment_added].indexOf(action.type) !== -1
    );
  };

  const canDeleteApplicantAction = (action: ApiAction) => {
    if (employee == undefined) return false;
    if (action.type == ApiActionTypes.cv_added) return false;
    if (canIEdit(ApiPermission.delete_all_comments)) return true;
    if (action.author?.id != employee.id) return false;

    return [ApiActionTypes.comment_added].indexOf(action.type) !== -1;
  };

  const handleEditAction = useCallback(
    (action: ApiAction) => {
      if (action.type === ApiActionTypes.comment_added) {
        setEditAction(action);
        return;
      }
    },
    [applicant]
  );

  const applicantActionDelete = useApplicantActionDelete();

  const handleDeleteAction = useCallback(
    (actionId: number) => {
      modalsService.openModal(ConfirmationModal, {
        question: t("common:delete"),
        text: t("common:delete_confirm"),
        acceptTitle: t("common:delete"),
        onAccept: async function () {
          try {
            await applicantActionDelete.mutateAsync({
              applicantId: applicant.id,
              actionId: actionId,
            });
            analyticsService.trackEvent(analyticEvents.applicants.actionDeleted, {
              [analyticProperties.id]: applicant.id,
            });
          } catch {
            return;
          }
        },
      });
    },
    [applicant]
  );

  return (
    <>
      <Stack gap={"md"} className={"mt-4"}>
        {!disabledComments && canIManageCandidates && (
          <AddComment entityId={applicant.id} entityType={ApiEntityTypes.applicant} />
        )}
        <Stack gap={"lg"}>
          {actions &&
            actions.map((action) =>
              editAction && editAction.id === action.id ? (
                <AddComment
                  action={action}
                  entityId={action.item_id}
                  entityType={ApiEntityTypes.applicant}
                  isCollapsedOnInit={false}
                  onSaved={() => {
                    setEditAction(undefined);
                  }}
                />
              ) : (
                <ActionItem
                  key={`comment-v2-${action.id}`}
                  action={action}
                  actionsSlot={
                    (canEditApplicantAction(action) || canDeleteApplicantAction(action)) && (
                      <DropdownMenu
                        trigger={
                          <FontAwesomeIcon
                            icon={"fa-light fa-ellipsis"}
                            className={"cursor-pointer"}
                          />
                        }
                      >
                        {canEditApplicantAction(action) && (
                          <DropdownMenuItem
                            onClick={() => handleEditAction(action)}
                            leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                            title={t("common:edit")}
                          />
                        )}
                        {canDeleteApplicantAction(action) && (
                          <DropdownMenuItem
                            onClick={() => handleDeleteAction(action.id)}
                            leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                            variant={"menuDanger"}
                            title={t("common:delete") + "..."}
                          />
                        )}
                      </DropdownMenu>
                    )
                  }
                />
              )
            )}
          <ActionItem
            key={`comment-v2-created`}
            action={{
              id: 0,
              author: applicant.author,
              datetime_action_date: applicant.datetime_created_at,
              item_id: applicant.id,
              item_type: ApiActionItemTypes.applicant,
              type: ApiActionTypes.created,
              visibility: ApiActionVisibilities.public,
              comment: applicant.source.title,
              files: [],
            }}
            attachments={
              applicant.source.source_type == SourceTypes.linkedin &&
              applicant.cvs.length > 0 &&
              applicant.cvs[0].source_url && (
                <Box>
                  <LinkTo
                    to={applicant.cvs[0].source_url}
                    target={"_blank"}
                    className={"break-all"}
                  >
                    {applicant.cvs[0].source_url}
                  </LinkTo>
                </Box>
              )
            }
          />
        </Stack>
      </Stack>
    </>
  );
}
